import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";

const ADMIN_BASE = "/admin";

const navItems = [
  {
    label: "Dashboard",
    path: ADMIN_BASE,
    icon: "fas fa-tachometer-alt",
    exact: true,
    badgeKey: null,
  },
  {
    label: "Users",
    path: `${ADMIN_BASE}/users`,
    icon: "fas fa-users",
    badgeKey: null,
  },
  {
    label: "Hackers",
    path: `${ADMIN_BASE}/hackers`,
    icon: "fas fa-user-secret",
    badgeKey: null,
  },
  {
    label: "Crypto Wallets",
    path: `${ADMIN_BASE}/wallets`,
    icon: "fas fa-wallet",
    badgeKey: null,
  },
  {
    label: "Transactions",
    path: `${ADMIN_BASE}/transactions`,
    icon: "fas fa-exchange-alt",
    badgeKey: "pendingTx",
  },
  {
    label: "Projects",
    path: `${ADMIN_BASE}/projects`,
    icon: "fas fa-folder-open",
    badgeKey: null,
  },
];

export default function AdminSidebar() {
  const location = useLocation();
  const { profile, signOut } = useAuth();
  const [pendingTxCount, setPendingTxCount] = useState(0);

  useEffect(() => {
    fetchPendingCount();
    // Poll every 60 seconds
    const interval = setInterval(fetchPendingCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchPendingCount = async () => {
    try {
      const supabase = getSupabase();
      const { count } = await supabase
        .from("crypto_transactions")
        .select("*", { count: "exact", head: true })
        .eq("status", "pending");
      setPendingTxCount(count || 0);
    } catch {
      // fallback
    }
  };

  const isActive = (path: string, exact?: boolean) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <aside className="w-60 min-h-screen bg-[#1a1a2e] text-white flex flex-col fixed left-0 top-0 z-40">
      {/* Logo */}
      <div className="px-5 py-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img
            src="https://storage.readdy-site.link/project_files/7340c102-b00e-4c74-80d1-988e660b490c/56518e71-1d48-4e5f-bd4e-2dd6f383c43a_compressed_HL.webp"
            alt="Hackers Lodge"
            className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
          />
          <div>
            <span className="text-sm font-bold tracking-tight">HACKERS LODGE</span>
            <span className="block text-[10px] text-gray-400 uppercase tracking-widest">Admin Panel</span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
              isActive(item.path, item.exact)
                ? "bg-[#5cb85c]/20 text-[#5cb85c]"
                : "text-gray-400 hover:bg-[#161b22]/5 hover:text-white"
            }`}
          >
            <div className="flex items-center gap-3">
              <i className={`${item.icon} text-sm w-4 text-center`} />
              {item.label}
            </div>
            {item.badgeKey === "pendingTx" && pendingTxCount > 0 && (
              <span className="bg-yellow-500 text-black text-[10px] font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center leading-none">
                {pendingTxCount}
              </span>
            )}
          </Link>
        ))}
      </nav>

      {/* User info */}
      <div className="px-4 py-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-[#5cb85c] flex items-center justify-center text-white text-xs font-bold">
            {profile?.display_name?.charAt(0)?.toUpperCase() || "A"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-white truncate">
              {profile?.display_name || "Admin"}
            </p>
            <p className="text-[10px] text-gray-400 truncate">
              {profile?.email}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link
            to="/"
            className="flex-1 text-center text-[10px] font-semibold text-gray-400 hover:text-white bg-[#161b22]/5 hover:bg-[#161b22]/10 rounded px-2 py-1.5 transition-all"
          >
            <i className="fas fa-external-link-alt mr-1" />View Site
          </Link>
          <button
            onClick={signOut}
            className="flex-1 text-center text-[10px] font-semibold text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded px-2 py-1.5 transition-all whitespace-nowrap"
          >
            <i className="fas fa-sign-out-alt mr-1" />Logout
          </button>
        </div>
      </div>
    </aside>
  );
}